/**
 * Guna Tattva D24 Information Table
 * Displays 24 Gunas (qualities) from Vaisheshika philosophy
 */

import React from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td, HStack, Text, TableContainer } from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  calculateGunaTattvas,
  GunaTattvaAttributes,
  GUNA_DESCRIPTIONS,
  GUNA_TYPES
} from '../data/GunaTattvaTableData';
import {
  TABLE_WRAPPER_INLINE_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_ALT_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  TABLE_TD_SMALL_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';
import { createPlanetDisplay } from '../helpers/planetDisplayHelper';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';

export interface GunaTattvaInfoTableProps {
  apiResponse?: JatakaResponse | null;
}

/**
 * Group planets by Guna number (1-24)
 */
function groupByGunaNumber(divisions: GunaTattvaAttributes[]): Map<number, GunaTattvaAttributes[]> {
  const grouped = new Map<number, GunaTattvaAttributes[]>();

  divisions.forEach(division => {
    const existing = grouped.get(division.gunaNumber) || [];
    grouped.set(division.gunaNumber, [...existing, division]);
  });

  return grouped;
}

export const GunaTattvaInfoTable: React.FC<GunaTattvaInfoTableProps> = ({ apiResponse }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  const gunaTattvaData = calculateGunaTattvas(apiResponse);

  if (!gunaTattvaData.divisions.length) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>Unable to calculate Guna Tattva divisions</Text>
      </Box>
    );
  }

  const groupedByGuna = groupByGunaNumber(gunaTattvaData.divisions);

  // Create all 24 rows (show empty rows for Gunas without planets)
  const allRows = GUNA_TYPES.map((gunaType, index) => {
    const gunaNumber = index + 1;
    const gunaInfo = GUNA_DESCRIPTIONS[gunaType];
    const planetsInGuna = groupedByGuna.get(gunaNumber) || [];

    return {
      gunaNumber,
      gunaType,
      gunaInfo,
      planets: planetsInGuna
    };
  });

  return (
    <Box {...TABLE_WRAPPER_INLINE_STYLES}>
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_ALT_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.large}>Planets</Th>
              <Th {...COLUMN_STYLES.xsmall}>Part</Th>
              <Th {...COLUMN_STYLES.medium}>GUNA</Th>
              <Th {...COLUMN_STYLES.huge}>Meaning</Th>
            </Tr>
          </Thead>
          <Tbody>
            {allRows.map(row => (
              <Tr key={row.gunaNumber} _hover={TABLE_ROW_HOVER_STYLES}>
                {/* Planets column */}
                <Td {...TABLE_TD_BASE_STYLES} minW="80px">
                  {row.planets.length > 0 ? (
                    <HStack spacing={1} flexWrap="wrap">
                      {row.planets.map((division, idx) => {
                        const planetDisplay = createPlanetDisplay(division.planet, currentHoraPlanet);
                        const colors = getAshtamshaPlanetColor(planetDisplay.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                        return (
                          <Text
                            key={`${division.planet.short_name}-${idx}`}
                            {...PLANET_TEXT_STYLES}
                            color={colors.color}
                            bg={colors.backgroundColor}
                            border={colors.borderColor ? `1px solid` : 'none'}
                            borderColor={colors.borderColor}
                          >
                            {planetDisplay.displayText}
                          </Text>
                        );
                      })}
                    </HStack>
                  ) : (
                    <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                  )}
                </Td>

                {/* Part column */}
                <Td {...TABLE_TD_BASE_STYLES}>
                  <Text fontSize="xs" fontWeight="normal">
                    {row.gunaNumber}
                  </Text>
                </Td>

                {/* GUNA (transliteration) column */}
                <Td {...TABLE_TD_SMALL_STYLES}>
                  <Text fontSize="xs" fontWeight="normal">
                    {row.gunaInfo.transliteration}
                  </Text>
                </Td>

                {/* Meaning column */}
                <Td {...TABLE_TD_SMALL_STYLES}>
                  <Text fontSize="xs" fontWeight="normal" color="gray.700">
                    {row.gunaInfo.meaning}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
